import { useNavigate } from 'react-router-dom';
import type { Itinerary } from '../../../mocks/itinerary';
import { agencyInfo } from '../../../mocks/dashboard';

const brand = agencyInfo.brandColor;

const statusConfig = {
  Draft:    { color: '#6B7280', bg: '#F9FAFB', label: 'Draft' },
  Sent:     { color: '#F59E0B', bg: '#FFFBEB', label: 'Sent' },
  Approved: { color: '#10B981', bg: '#ECFDF5', label: 'Approved' },
};

interface Props {
  itinerary: Itinerary;
}

export default function ItineraryCard({ itinerary }: Props) {
  const navigate = useNavigate();
  const status = statusConfig[itinerary.status];
  const totalSell = itinerary.days.flatMap((d) => d.services).reduce((sum, s) => sum + s.sellPrice, 0);
  const totalCost = itinerary.days.flatMap((d) => d.services).reduce((sum, s) => sum + s.costPrice, 0);
  const profit = totalSell - totalCost;

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-gray-200 transition-all cursor-pointer group"
      onClick={() => navigate(`/itinerary/builder?id=${itinerary.id}`)}>
      <div className="relative h-36 overflow-hidden">
        <img
          src={itinerary.coverImage}
          alt={itinerary.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
          <div>
            <p className="text-white font-semibold text-sm leading-tight">{itinerary.title}</p>
            <p className="text-white/70 text-xs mt-0.5">{itinerary.destination}</p>
          </div>
          <span
            className="text-xs font-semibold px-2 py-1 rounded-full"
            style={{ color: status.color, backgroundColor: status.bg }}
          >
            {status.label}
          </span>
        </div>
      </div>

      <div className="p-4">
        <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line" />
            {itinerary.startDate}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-moon-line" />
            {itinerary.nights}N
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-user-line" />
            {itinerary.pax} pax
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-file-list-2-line" />
            {itinerary.days.length} days
          </span>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="bg-gray-50 rounded-lg p-2 text-center">
            <p className="text-xs text-gray-400">Sell</p>
            <p className="text-xs font-semibold text-gray-700">{totalSell > 0 ? `${itinerary.currency} ${totalSell.toLocaleString()}` : '—'}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2 text-center">
            <p className="text-xs text-gray-400">Cost</p>
            <p className="text-xs font-semibold text-gray-700">{totalCost > 0 ? `${itinerary.currency} ${totalCost.toLocaleString()}` : '—'}</p>
          </div>
          <div className="rounded-lg p-2 text-center" style={{ backgroundColor: `${brand}10` }}>
            <p className="text-xs text-gray-400">Profit</p>
            <p className="text-xs font-semibold" style={{ color: brand }}>{profit > 0 ? `${itinerary.currency} ${profit.toLocaleString()}` : '—'}</p>
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-gray-50">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
              style={{ backgroundColor: brand }}>
              {itinerary.createdBy.split(' ').map(n => n[0]).join('')}
            </div>
            <span className="text-xs text-gray-500">{itinerary.createdBy}</span>
          </div>
          {itinerary.linkedBooking && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
              {itinerary.linkedBooking}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
